// Performance Optimization Utilities - SAFE VERSION
// Fixed issues causing production white screen

// 1. Image Lazy Loading (Native + Fallback)
export const enableNativeLazyLoading = () => {
  if (typeof window === 'undefined') return;

  if ('loading' in HTMLImageElement.prototype) {
    // Browser supports native lazy loading
    const images = document.querySelectorAll('img[data-src]');
    images.forEach(img => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  } else {
    // Fallback: Intersection Observer
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          observer.unobserve(img);
        }
      });
    });

    const images = document.querySelectorAll('img[data-src]');
    images.forEach(img => imageObserver.observe(img));
  }
};

// 2. Service Worker Registration (PWA) - SAFE VERSION
export const registerServiceWorker = () => {
  if (typeof navigator === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production') return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then(registration => {
        console.log('[SW] Registered:', registration.scope);
      })
      .catch(error => {
        console.warn('[SW] Registration failed:', error.message);
        // Don't throw - just log warning
      });
  });
};

// 3. WebP Support Check
export const checkWebPSupport = () => {
  if (typeof window === 'undefined') return;

  const img = new Image();
  img.src = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
  img.onload = () => {
    document.documentElement.classList.add('webp-supported');
  };
  img.onerror = () => {
    document.documentElement.classList.add('webp-not-supported');
  };
};

// 4. Core Web Vitals Monitoring - SAFE VERSION
export const monitorCoreWebVitals = () => {
  if (typeof window === 'undefined') return;
  if (!('PerformanceObserver' in window)) return;

  try {
    // LCP (Largest Contentful Paint)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      const lcp = lastEntry.renderTime || lastEntry.loadTime;
      if (lcp) {
        console.log('[Perf] LCP:', Math.round(lcp), 'ms');
      }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

    // FID (First Input Delay)
    const fidObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const fid = entry.processingStart - entry.startTime;
        console.log('[Perf] FID:', Math.round(fid), 'ms');
      });
    });
    fidObserver.observe({ type: 'first-input', buffered: true });

    // CLS (Cumulative Layout Shift)
    let clsScore = 0;
    const clsObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsScore += entry.value;
        }
      });
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });

    // Report final CLS on page unload
    window.addEventListener('beforeunload', () => {
      console.log('[Perf] CLS:', clsScore.toFixed(3));
    });
  } catch (error) {
    console.warn('[Perf] Monitoring failed:', error.message);
  }
};

// 5. Initialize All Performance Optimizations - SAFE VERSION
export const initPerformanceOptimizations = () => {
  // Run on page load
  if (typeof window === 'undefined') return;

  try {
    // Safe optimizations only
    enableNativeLazyLoading();
    checkWebPSupport();
    registerServiceWorker();

    // Monitor in production only
    if (process.env.NODE_ENV === 'production') {
      monitorCoreWebVitals();
    }

    console.log('[Perf] Optimizations initialized');
  } catch (error) {
    console.error('[Perf] Initialization error:', error);
    // Don't throw - graceful degradation
  }
};

export default initPerformanceOptimizations;
